import { ApolloError } from '@apollo/client'
import useGoogleApi, { LatLng } from '@hooks/useGoogleApi'
import { InfoWindowF, GoogleMap as Map, MarkerF } from '@react-google-maps/api'
import parse from 'html-react-parser'
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import { ButtonModifier, ButtonTypes } from '@components/atoms/Button/Button'
import Text from '@components/atoms/Text'
import { TextVariants } from '@components/atoms/Text/Text'

import { parseOptions } from '@helper/parseOptions'
import { PropsWithClassName } from '@helper/PropsWithClassName'

import { Studio } from '@definitions/types/symfonyTypesd'

import {
  DummyMap,
  LoadingText,
  ParsedAddress,
  Root,
  StyledButton,
  StyledInfoWindow,
  StyledLoadingSpinner,
} from './GoogleMap.styles'

interface Props extends PropsWithClassName {
  studios: Studio[]
  setStudio: (studio: Studio) => void
  loading: boolean
  error: ApolloError | undefined
  center?: LatLng
}

const GoogleMap: React.FC<PropsWithChildren<Props>> = (
  props: PropsWithChildren<Props>
): React.ReactElement => {
  const { studios, setStudio } = props
  const [userLocation, setUserLocation] = useState<LatLng | null>(null)

  const [center, setCenter] = useState<LatLng>({ lat: 51.1657, lng: 10.4515 })
  const [activeStudio, setActiveStudio] = useState<Studio | null>(null)

  const { googleApiLoaded } = useGoogleApi()

  const theme: any = useTheme()

  useEffect(() => {
    if (googleApiLoaded && props.center === undefined) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          if (!userLocation) {
            setUserLocation({ lat: latitude, lng: longitude })
          }
        },
        (error) => {
          console.error('Error getting user location:', error)
        }
      )
    }
  }, [googleApiLoaded, props.center, userLocation])

  if (!googleApiLoaded) {
    return (
      <Root className={props.className} data-testid={'google-map-root'}>
        <DummyMap>
          <StyledLoadingSpinner />
          <LoadingText variant={TextVariants.lead}>Loading...</LoadingText>
        </DummyMap>
      </Root>
    )
  }

  const handleSelectStudio = (studio: Studio) => {
    setStudio(studio)
    setActiveStudio(null)
  }

  const mapOptions = {
    disableDefaultUI: true,
    clickableIcons: true,
    fullscreenControl: false,
    styles: [
      {
        featureType: 'all',
        stylers: [
          {
            saturation: -100, // Remove saturation from all elements
            hue: '#ffffff', // Set hue to white to remove any color
          },
        ],
      },
      {
        featureType: 'administrative',
        elementType: 'geometry.stroke',
        stylers: [
          {
            visibility: 'off', // Hide administrative boundaries
          },
        ],
      },
      {
        featureType: 'administrative.country',
        elementType: 'geometry.fill',
        stylers: [
          {
            visibility: 'off', // Hide country boundaries
          },
        ],
      },
      {
        featureType: 'landscape',
        elementType: 'geometry',
        stylers: [
          {
            lightness: 10, // Increase lightness to make it look like greyscale
          },
        ],
      },
      {
        featureType: 'poi',
        elementType: 'all',
        stylers: [
          {
            visibility: 'off', // Hide points of interest
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [
          {
            color: '#cccccc', // Grey color for roads
          },
        ],
      },
      {
        featureType: 'transit',
        elementType: 'all',
        stylers: [
          {
            visibility: 'off', // Hide transit lines and stations
          },
        ],
      },
      {
        featureType: 'water',
        elementType: 'geometry.fill',
        stylers: [
          {
            color: '#dddddd', // Light grey color for water bodies
          },
        ],
      },
    ],
  }

  return (
    <Root className={props.className} data-testid={'google-map-root'}>
      <Map
        options={mapOptions}
        zoom={6}
        center={props.center ?? (userLocation || center)} // Use user's location if available
        mapTypeId={google.maps.MapTypeId.ROADMAP}
        mapContainerStyle={{ width: '100%', height: '450px' }}
      >
        <MarkerF
          position={{
            lat: props.center?.lat || userLocation?.lat || center.lat,
            lng: props.center?.lng || userLocation?.lng || center.lng,
          }}
          icon={'/img/marker/marker_map.png'}
        ></MarkerF>
        {studios.length > 0 &&
          studios.map((studio) => {
            if (
              typeof studio.latitude === 'number' &&
              typeof studio.longitude === 'number'
            ) {
              let marker
              if (theme.key === 'sunpoint') {
                marker = '/img/marker/marker_sunpoint.png'
              }
              if (theme.key === 'wellmaxx') {
                marker = '/img/marker/marker_wellmaxx.png'
              }
              return (
                <MarkerF
                  key={studio.id + studio.name}
                  position={{
                    lat: studio.latitude,
                    lng: studio.longitude,
                  }}
                  icon={marker}
                  onClick={() => setActiveStudio(studio)}
                >
                  {activeStudio?.id === studio.id && (
                    <InfoWindowF onCloseClick={() => setActiveStudio(studio)}>
                      <StyledInfoWindow>
                        <Text variant={TextVariants.paragraph}>
                          <b>{studio.title}</b>
                        </Text>
                        <ParsedAddress>
                          {parse(studio.address, parseOptions)}
                        </ParsedAddress>
                        <StyledButton
                          disabled={false}
                          loading={false}
                          buttonType={ButtonTypes.BUTTON}
                          modifier={ButtonModifier.SMALL}
                          onClick={() => handleSelectStudio(studio)}
                        >
                          Studio wählen
                        </StyledButton>
                      </StyledInfoWindow>
                    </InfoWindowF>
                  )}
                </MarkerF>
              )
            }
          })}
      </Map>
    </Root>
  )
}

export { GoogleMap }
